/**
 * OVERRIDE Schedule.tsx
 * FILE: @plone-collective/volto-techevent/src/components/Schedule/Schedule.tsx
 * DATE: 2026-09-11
 *
 * CHANGES vs. upstream:
 *
 * 1. Sticky day tab (localStorage)
 *    The upstream Tab is uncontrolled and always opens on the first day.
 *    This override makes it controlled and persists the user's explicit
 *    day choice in localStorage under `schedule.activeDay:<pathname>`,
 *    so a reload (common on mobile) restores the selected day.
 *
 * 2. Auto-select today on conference days
 *    On load, resolveActiveDay() picks the initial tab: during the
 *    conference it opens on *today*, unless the stored choice was made
 *    earlier today (a choice stored yesterday must not hijack this
 *    morning's visit). Outside conference days a valid stored choice
 *    wins; otherwise the first day (upstream behavior).
 *
 * 3. Auto-scroll to the next upcoming slot
 *    When the initial tab is today, the page scrolls — once per load,
 *    never on manual tab switches — so the *next* slot's title is
 *    visible below the sticky site header (SCROLL_OFFSET_PX leaves
 *    about one timeslot of context above it). Requires the
 *    data-start/data-end attributes added in the DaySchedule.jsx
 *    override (imported here via ./DaySchedule).
 *
 * 4. Day list memoization
 *    daysInfo is wrapped in useMemo so the resolution effect can depend
 *    on it without re-running on every render.
 *
 * Everything else is identical to the upstream file.
 *
 * NOTE: isEditMode never reaches this component (upstream DefaultView
 * forwards data.isEditMode, which is always undefined), so no edit-mode
 * guard is applied; the effects are one-shot per page load and harmless
 * in the edit form.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tab, Menu } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import DaySchedule from './DaySchedule';
import {
  SCROLL_OFFSET_PX,
  findUpcomingSlotIndex,
  localDayId,
  readStoredDay,
  resolveActiveDay,
  storageKeyForPath,
  writeStoredDay,
} from './scheduleUtils';

const Schedule = (props) => {
  const { isEditMode, filterByDay, displayDayIndex, items } = props;

  const daysInfo = useMemo(
    () =>
      items &&
      items
        .map((day) => {
          const keyDay = day['id'];
          if (!filterByDay || filterByDay.length === 0) {
            return day;
          }
          if (filterByDay.includes(keyDay.slice(0, 10))) {
            return day;
          }
          return null;
        })
        .filter((item) => item !== null),
    [items, filterByDay],
  );

  const dayIds = useMemo(
    () => (daysInfo ? daysInfo.map((day) => day['id']) : []),
    [daysInfo],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const resolvedRef = useRef(false);
  const pendingScrollDay = useRef(null);
  const containerRef = useRef(null);

  // Resolve the initial tab once, on the client, as soon as days exist.
  useEffect(() => {
    if (resolvedRef.current || dayIds.length === 0) return;
    resolvedRef.current = true;
    const today = localDayId(new Date());
    const key = storageKeyForPath(window.location.pathname);
    const stored = readStoredDay(key, window.localStorage);
    const dayId = resolveActiveDay({ days: dayIds, today, stored });
    const index = dayIds.indexOf(dayId);
    if (index > 0) {
      setActiveIndex(index);
    }
    if (dayId === today) {
      pendingScrollDay.current = dayId;
    }
  }, [dayIds]);

  // Once the active pane is rendered, scroll to the next upcoming slot.
  // Runs at most once per page load and only when we opened on today.
  // The scroll is retried until the target actually lands near the top:
  // right after hydration the document is often not tall enough to scroll
  // yet (grid CSS / fonts still settling), which silently swallows
  // window.scrollTo. The pending day is consumed only on success.
  useEffect(() => {
    const dayId = pendingScrollDay.current;
    if (!dayId || dayIds[activeIndex] !== dayId) return;
    const day = daysInfo?.[activeIndex];
    const slotIndex = findUpcomingSlotIndex(day?.items, new Date());
    if (slotIndex < 0) {
      pendingScrollDay.current = null;
      return;
    }
    const start = day.items[slotIndex].start;
    // kitconcept/volto-light-theme scrolls document.body, not the document
    // element (html/body are height:100%), so window.scrollTo is a no-op.
    // Scroll whichever element actually overflows.
    const scroller =
      document.scrollingElement.scrollHeight > window.innerHeight
        ? document.scrollingElement
        : document.body;
    let attempts = 0;
    let timer;
    const tryScroll = () => {
      const target = containerRef.current?.querySelector(
        `[data-start="${start}"]`,
      );
      if (!target) return false;
      const top =
        target.getBoundingClientRect().top +
        scroller.scrollTop -
        SCROLL_OFFSET_PX;
      scroller.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      return (
        Math.abs(target.getBoundingClientRect().top - SCROLL_OFFSET_PX) < 60
      );
    };
    const attempt = () => {
      if (tryScroll()) {
        pendingScrollDay.current = null;
        return;
      }
      if (++attempts < 20) {
        timer = setTimeout(attempt, 150);
      } else {
        pendingScrollDay.current = null;
      }
    };
    attempt();
    return () => clearTimeout(timer);
  }, [activeIndex, dayIds, daysInfo]);

  const handleTabChange = (event, { activeIndex: index }) => {
    setActiveIndex(index);
    const dayId = dayIds[index];
    if (dayId) {
      writeStoredDay(
        storageKeyForPath(window.location.pathname),
        dayId,
        localDayId(new Date()),
        window.localStorage,
      );
    }
  };

  const panes =
    daysInfo &&
    daysInfo.map((day, dayIndex) => {
      const keyDay = day['id'];
      const date = new Date(`${keyDay}T12:00:00+00:00`);
      return {
        menuItem: (
          <Menu.Item key={`${keyDay}_menu`}>
            <div className={'day day-label'}>
              {displayDayIndex && (
                <div className={'heading'}>
                  <FormattedMessage id="Day" defaultMessage="Day" />{' '}
                  {dayIndex + 1}
                </div>
              )}
              <div className={'description'}>
                <FormattedDate
                  date={date}
                  includeTime={false}
                  format={{
                    month: 'short',
                    day: 'numeric',
                  }}
                />
              </div>
            </div>
          </Menu.Item>
        ),
        render: () => {
          return (
            <Tab.Pane className="tab-container" key={`${keyDay}_container`}>
              <DaySchedule day={day} />
            </Tab.Pane>
          );
        },
      };
    });

  return (
    <div className="schedule" ref={containerRef}>
      {isEditMode && 'Schedule Edit Mode'}
      <Tab
        panes={panes}
        className="tab"
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default Schedule;
